// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — What's coming
// ---------------------------------------------------------------------------
// A visible roadmap so colleagues can see what is being built without asking,
// and a clear route to ask for something that is not here.
//
// THIS CONTENT IS WRITTEN BY HAND, DELIBERATELY. It is NOT generated from the
// engineering backlog. The backlog names clients, quotes estate sizes, records
// which client's data is loaded where, and carries internal notes about
// commercial and security work. None of that belongs on a page that anyone with
// a login can open, and a page that auto-syncs from an internal task list will
// eventually leak one of those the day somebody files a ticket with a client
// name in the title. So the two are kept separate on purpose: when the backlog
// changes, someone updates this file with a sentence a client could read.
//
// Rules for editing this file:
//   1. No client names. Ever. Not even ones you think are public.
//   2. No estate sizes, spend figures, or anything traceable to one engagement.
//   3. Describe the capability, not the implementation or the ticket.
//   4. Say honestly where something is. "Exploring" is a real status; pretending
//      everything is nearly done is how a roadmap stops being believed.
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Divider, TextField, Button,
  ToggleButton, ToggleButtonGroup, Alert, AlertTitle, Link,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { getSession } from './auth';

const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

const REQUESTS_TO = 'FScalzo@optimumhit.com';

// Status vocabulary, deliberately small. Anything vaguer than these four is a
// way of avoiding a commitment.
const STATUS = {
  live: { label: 'Available now', color: '#2E7D32', order: 0 },
  building: { label: 'Being built', color: BLUE, order: 1 },
  next: { label: 'Planned', color: '#EF6C00', order: 2 },
  exploring: { label: 'Exploring', color: GREY, order: 3 },
};

// ---------------------------------------------------------------------------
// The roadmap itself. Capability-level, client-safe.
// ---------------------------------------------------------------------------
const ITEMS = [
  // ---- Available now ------------------------------------------------------
  { status: 'live', area: 'Discovery', title: 'Application inventory and fact sheets',
    detail: 'A full application catalogue with the fields a migration actually needs — ownership, criticality, hosting, disposition, DR posture, lessons learned and cloud gotchas.' },
  { status: 'live', area: 'Discovery', title: 'Spreadsheet import with column mapping',
    detail: 'Upload an inventory in whatever shape the client sends it. Columns are auto-mapped to a common field set and you confirm anything ambiguous. Re-importing an updated file updates records instead of duplicating them.' },
  { status: 'live', area: 'Discovery', title: 'Infrastructure import and server mapping',
    detail: 'Load virtual estate exports, then map servers to the applications and environments they belong to using rules, a worksheet round-trip, or by hand.' },
  { status: 'live', area: 'Discovery', title: 'Environment and version tracking',
    detail: 'Production, test, training, DR and one-off instances are tracked separately, each with its own version, cutover date and wave — because they migrate on different dates with different risk.' },
  { status: 'live', area: 'Analysis', title: 'Landscape, matrix and circle heat maps',
    detail: 'Portfolio views colour-coded by any captured field, so a gap in the data is as visible as the data itself.' },
  { status: 'live', area: 'Analysis', title: 'Portfolio and radar charts',
    detail: 'Plot any two dimensions against each other — functional against technical fit, disposition against criticality, RTO against RPO — with quadrant reading for rationalisation decisions.' },
  { status: 'live', area: 'Analysis', title: 'Obsolescence and technology risk',
    detail: 'Which operating systems are past end of support, which end soon, and — just as importantly — how much of the estate cannot be assessed yet because the source data is too vague.' },
  { status: 'live', area: 'Analysis', title: 'Right-sizing from utilisation',
    detail: 'Where an inventory carries real CPU, memory and IOPS telemetry, size the target from observed peak rather than from what was provisioned years ago.' },
  { status: 'live', area: 'Analysis', title: 'Cost modelling on live cloud prices',
    detail: 'Estimates priced against current AWS and Azure list prices rather than a stale rate card, with private pricing discounts, support tiers and additional cost lines modelled on top.' },
  { status: 'live', area: 'Architecture', title: 'Relationship and data flow diagrams',
    detail: 'Printable relationship maps and an integration view showing what routes through an interface engine and what connects point-to-point — the latter being the harder migration problem.' },
  { status: 'live', area: 'Architecture', title: 'Target architecture from Epic sizing guides',
    detail: 'Import an Epic Cloud Specifications Guide and render the future state by stamp and tier, with version-to-version comparison when a guide is reissued.' },
  { status: 'live', area: 'Planning', title: 'Migration waves and cutover readiness',
    detail: 'Dependency-ordered wave planning and a readiness view that answers whether an application can actually cut over yet.' },
  { status: 'live', area: 'Delivery', title: 'Reports gallery and saved views',
    detail: 'Save a report configuration, name it, favourite it and share it within your workspace. Grouped by the kind of engagement work it supports rather than by chart type.' },
  { status: 'live', area: 'Delivery', title: 'Discovery surveys and forms round-trip',
    detail: 'Send questionnaires to application owners, or export a form, have it filled in offline, and load the answers back.' },
  { status: 'live', area: 'Platform', title: 'Per-client workspaces with strict separation',
    detail: 'Each engagement has its own workspace. Separation is enforced by the database itself, not only by the application, so one client can never see another.' },
  { status: 'live', area: 'Platform', title: 'Change history',
    detail: 'Every field change is recorded with who changed it, when, and what it was before.' },

  // ---- Being built --------------------------------------------------------
  { status: 'building', area: 'Platform', title: 'Sign in with your Optimum account',
    detail: 'Single sign-on so you use your normal work credentials instead of a separate password. Local sign-in stays available as a fallback.' },
  { status: 'building', area: 'Discovery', title: 'Naming convention decoder',
    detail: 'Every organisation encodes meaning in its host names. Build up that vocabulary per client and use it to explain an estate that would otherwise be thousands of anonymous machines.' },

  // ---- Planned ------------------------------------------------------------
  { status: 'next', area: 'Analysis', title: 'Actual cloud spend alongside estimates',
    detail: 'With read-only billing access to a cloud account, show what is really being spent next to what was projected. Read-only, billing scope only, and revocable by the account owner at any time.' },
  { status: 'next', area: 'Analysis', title: 'FinOps tooling',
    detail: 'Idle and orphaned resource detection, commitment coverage, and showback by application and business unit — with every figure tied back to an application rather than living in a separate spreadsheet.' },
  { status: 'next', area: 'Architecture', title: 'Built-versus-specified audit',
    detail: 'Compare what was actually deployed in a cloud account against what the sizing guide specified, and surface the drift.' },
  { status: 'next', area: 'Delivery', title: 'Reusable engagement templates',
    detail: 'Start a new engagement from a proven template — fact sheet structures, questionnaires and reference architectures — instead of rebuilding from scratch each time.' },
  { status: 'next', area: 'Platform', title: 'Client sign-on with their own directory',
    detail: 'Let a client organisation sign in with their own identity provider rather than a Primordia-specific account.' },

  // ---- Exploring ----------------------------------------------------------
  { status: 'exploring', area: 'Discovery', title: 'Capture from meetings and dictation',
    detail: 'Discovery knowledge is spoken in interviews and then lost. Pull it from meeting transcripts, propose the facts with the quote that supports each one, and let a consultant confirm before anything is recorded.' },
  { status: 'exploring', area: 'Discovery', title: 'Reading scanned documents',
    detail: 'Extract text and — more usefully — tables from scanned runbooks and architecture PDFs, which is how most infrastructure documentation actually arrives.' },
  { status: 'exploring', area: 'Platform', title: 'Two-way connectors to other EA tools',
    detail: 'Exchange data with the tools clients already run, mapping fields between their model and ours.' },
  { status: 'exploring', area: 'Delivery', title: 'Mobile access',
    detail: 'Read-only access to the catalogue and reports from a phone or tablet while on site.' },
];

const AREAS = ['All', 'Discovery', 'Analysis', 'Architecture', 'Planning', 'Delivery', 'Platform'];

function StatusChip({ status }) {
  const s = STATUS[status] || STATUS.exploring;
  return (
    <Chip
      size="small"
      label={s.label}
      sx={{
        bgcolor: s.color, color: '#fff', fontWeight: 700, fontSize: 11,
        height: 22, letterSpacing: 0.3,
      }}
    />
  );
}

export default function Roadmap() {
  const [area, setArea] = useState('All');
  const [idea, setIdea] = useState('');
  const session = getSession();

  const grouped = useMemo(() => {
    const rows = ITEMS.filter((i) => area === 'All' || i.area === area);
    const order = Object.keys(STATUS).sort((a, b) => STATUS[a].order - STATUS[b].order);
    return order
      .map((k) => ({ key: k, items: rows.filter((r) => r.status === k) }))
      .filter((g) => g.items.length);
  }, [area]);

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS).forEach((k) => { c[k] = ITEMS.filter((i) => i.status === k).length; });
    return c;
  }, []);

  // A mailto rather than a ticket queue, on purpose: a request that lands in a
  // real inbox gets a reply from a person. Prefilled so the sender does not have
  // to explain who they are or where they were.
  const mailto = useMemo(() => {
    const subject = 'Primordia — feature request';
    const body = [
      idea.trim() || '(describe what you would like Primordia to do)',
      '',
      '---',
      `Sent from Primordia by ${session?.email || 'a signed-out user'}`,
      session?.tenantName ? `Workspace: ${session.tenantName}` : '',
    ].filter(Boolean).join('\n');
    return `mailto:${REQUESTS_TO}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [idea, session]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ color: HEAD, fontWeight: 800 }}>
        What&rsquo;s coming in Primordia
      </Typography>
      <Typography variant="body2" sx={{ color: GREY, mt: 0.5, mb: 2.5 }}>
        What is available today, what is being built, and what is still an idea. If something you
        need is missing, ask — the list below is shaped by what people actually run into on
        engagements, not by what is easiest to build.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        {Object.keys(STATUS)
          .sort((a, b) => STATUS[a].order - STATUS[b].order)
          .map((k) => (
            <Chip
              key={k}
              size="small"
              label={`${STATUS[k].label}: ${counts[k]}`}
              variant="outlined"
              sx={{ borderColor: STATUS[k].color, color: STATUS[k].color, fontWeight: 700 }}
            />
          ))}
      </Stack>

      <ToggleButtonGroup
        size="small"
        exclusive
        value={area}
        onChange={(e, v) => v && setArea(v)}
        sx={{ mb: 3, flexWrap: 'wrap' }}
      >
        {AREAS.map((a) => (
          <ToggleButton key={a} value={a} sx={{ textTransform: 'none', px: 1.5 }}>{a}</ToggleButton>
        ))}
      </ToggleButtonGroup>

      {grouped.map((g) => (
        <Box key={g.key} sx={{ mb: 3.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.2 }}>
            <StatusChip status={g.key} />
            <Divider sx={{ flex: 1 }} />
          </Stack>
          <Stack spacing={1.2}>
            {g.items.map((item) => (
              <Paper key={item.title} variant="outlined" sx={{ p: 1.8, borderLeft: `4px solid ${STATUS[g.key].color}` }}>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 700, color: NAVY }}>{item.title}</Typography>
                  <Chip size="small" label={item.area} variant="outlined" sx={{ height: 19, fontSize: 10, color: GREY }} />
                </Stack>
                <Typography variant="body2" sx={{ color: GREY, mt: 0.6 }}>{item.detail}</Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      ))}

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Dates are deliberately absent</AlertTitle>
        Items move between these groups as engagement work reveals what is genuinely needed. A date
        on this page would be a guess, and a roadmap people stop believing is worse than no roadmap.
        If timing matters for something you are working on, ask directly and you will get a straight
        answer about where it sits.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2.2, borderColor: BLUE }}>
        <Typography sx={{ color: HEAD, fontWeight: 800, mb: 0.5 }}>Ask for something</Typography>
        <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
          Missing a field, a report, an import format, or a way of working? Describe it below and it
          opens an email to Frank Scalzo. Real examples from a live engagement carry the most weight
          — what you were trying to do, and what stopped you.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder="What would you like Primordia to do?"
          value={idea}
          onChange={(e) => setIdea(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            href={mailto}
            sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}
          >
            Send the request
          </Button>
          <Typography variant="caption" sx={{ color: GREY }}>
            Opens your mail client — nothing is sent from this page, and nothing is stored here.
            Or email <Link href={`mailto:${REQUESTS_TO}`}>{REQUESTS_TO}</Link> directly.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
